import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  Container, 
  Wrapper, 
  Title, 
  Desc, 
  TechRail, 
  RailContent, 
  TechCard, 
  IconImage, 
  CategoryGrid, 
  CategoryCard, 
  CategoryTitle, 
  TagContainer, 
  Tag 
} from './TechStackStyle';

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const topTools = [
  { name: 'JavaScript', icon: 'https://cdn.worldvectorlogo.com/logos/javascript-1.svg' },
  { name: 'Python', icon: 'https://cdn.worldvectorlogo.com/logos/python-5.svg' },
  { name: 'Node.js', icon: 'https://cdn.worldvectorlogo.com/logos/nodejs-icon.svg' },
  { name: 'React', icon: 'https://cdn.worldvectorlogo.com/logos/react-2.svg' },
  { name: 'Tailwind', icon: 'https://cdn.worldvectorlogo.com/logos/tailwindcss.svg' },
  { name: 'Figma', icon: 'https://static.vecteezy.com/system/resources/previews/065/386/866/non_2x/figma-white-circle-logo-icon-figma-app-editable-transparent-background-premium-social-media-design-for-digital-download-free-png.png' },
  { name: 'PostgreSQL', icon: 'https://cdn.worldvectorlogo.com/logos/postgresql.svg' },
  { name: 'Docker', icon: 'https://cdn.worldvectorlogo.com/logos/docker-4.svg' },
  { name: 'Jira', icon: 'https://cdn.worldvectorlogo.com/logos/jira-1.svg' },
  { name: 'GitHub', icon: 'https://cdn.worldvectorlogo.com/logos/github-icon-1.svg' },
  { name: 'Mixpanel', icon: 'https://masterconcept.ai/wp-content/uploads/2022/10/Mixpanel-logo.png' },
  { name: 'Miro', icon: 'https://uxwing.com/wp-content/themes/uxwing/download/brands-and-social-media/miro-icon.png' },
  { name: 'Confluence', icon: 'https://uxwing.com/wp-content/themes/uxwing/download/brands-and-social-media/atlassian-confluence-icon.png' },
];

const categories = [
  {
    title: 'Strategy & Execution',
    tags: ['Productboard', 'Miro', 'Notion', 'Figma', 'PRD Writing', 'Roadmapping'],
    delay: '0s'
  },
  {
    title: 'Product Analytics',
    tags: ['Mixpanel', 'Amplitude', 'Segment', 'SQL', 'Google Analytics', 'Hotjar' ],
    delay: '0.1s'
  },
  {
    title: 'Development Hub',
    tags: ['JavaScript', 'Python', 'Node.js', 'React', 'Git', 'PostgreSQL'],
    delay: '0.2s'
  },
  {
    title: 'AI Engineering',
    tags: ['OpenAI', 'Anthropic', 'Gemini', 'LangChain', 'Whisper', 'Vector DBs'],
    delay: '0.15s'
  },
  {
    title: 'Agile & Ops',
    tags: ['Jira / Sprints', 'Confluence', 'Docker', 'Asana', 'Airtable'],
    delay: '0.05s'
  },
  {
    title: 'Modern Stack',
    tags: ['Vite', 'Tailwind CSS', 'Framer', 'Cloudflare', 'Next.js', 'Cursor'],
    delay: '0.25s'
  }
];

const getIcon = (name) => {
  const icons = {
    'Productboard': 'https://avatars.githubusercontent.com/u/11894429?s=200&v=4',
    'Miro': 'https://uxwing.com/wp-content/themes/uxwing/download/brands-and-social-media/miro-icon.png',
    'Notion': 'https://uxwing.com/wp-content/themes/uxwing/download/brands-and-social-media/notion-icon.png',
    'Figma': 'https://www.vectorlogo.zone/logos/figma/figma-icon.svg',
    'Whimsical': 'https://v1.whimsical.com/favicon-32x32.png',
    'Loom': 'https://www.vectorlogo.zone/logos/loom/loom-icon.svg',
    'PRD Writing': 'https://cdn-icons-png.flaticon.com/256/85/85536.png',
    'Roadmapping': 'https://png.pngtree.com/png-clipart/20230105/original/pngtree-3d-road-map-information-in-black-and-white-color-png-image_8874392.png',
    'Mixpanel': 'https://masterconcept.ai/wp-content/uploads/2022/10/Mixpanel-logo.png',
    'Amplitude': 'https://www.vectorlogo.zone/logos/amplitude/amplitude-icon.svg',
    'Segment': 'https://www.vectorlogo.zone/logos/segment/segment-icon.svg',
    'Google Analytics': 'https://www.vectorlogo.zone/logos/google_analytics/google_analytics-icon.svg',
    'Hotjar': 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRQD0tcVxRNPdnIOTr3soI8qQW_u4EykMfsSQ&s',
    'SQL': 'https://www.svgrepo.com/show/331760/sql-database-generic.svg',
    'OpenAI': 'https://static.vecteezy.com/system/resources/previews/022/227/364/non_2x/openai-chatgpt-logo-icon-free-png.png',
    'Anthropic': 'https://www.primarymarkets.com/wp-content/uploads/2024/03/Anthropic-Circle-Logo.png',
    'Gemini': 'https://static.vecteezy.com/system/resources/thumbnails/046/861/646/small_2x/gemini-icon-on-a-transparent-background-free-png.png',
    'LangChain': 'https://logo.svgcdn.com/simple-icons/langchain-dark.png',
    'Whisper': 'https://images.g2crowd.com/uploads/vendor/image/17630/c4bc02392e38ee696952fcd6ed2fd2d6.jpeg',
    'Vector DBs': 'https://icons.veryicon.com/png/o/business/bank-logo-collection/dbs-logo.png',
    'JavaScript': 'https://www.vectorlogo.zone/logos/javascript/javascript-icon.svg',
    'Python': 'https://www.vectorlogo.zone/logos/python/python-icon.svg',
    'Node.js': 'https://www.vectorlogo.zone/logos/nodejs/nodejs-icon.svg',
    'React': 'https://www.vectorlogo.zone/logos/reactjs/reactjs-icon.svg',
    'Git': 'https://www.vectorlogo.zone/logos/git-scm/git-scm-icon.svg',
    'PostgreSQL': 'https://www.vectorlogo.zone/logos/postgresql/postgresql-icon.svg',
    'Jira / Sprints': 'https://www.vectorlogo.zone/logos/atlassian_jira/atlassian_jira-icon.svg',
    'Confluence': 'https://uxwing.com/wp-content/themes/uxwing/download/brands-and-social-media/atlassian-confluence-icon.png',
    'Docker': 'https://www.vectorlogo.zone/logos/docker/docker-tile.svg',
    'Asana': 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSwEodKlQHNi3FEyWj5ODtQapWRqv12Fqlk4g&s',
    'Airtable': 'https://www.vectorlogo.zone/logos/airtable/airtable-icon.svg',
    'Vite': 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/f1/Vitejs-logo.svg/1280px-Vitejs-logo.svg.png',
    'Tailwind CSS': 'https://www.vectorlogo.zone/logos/tailwindcss/tailwindcss-icon.svg',
    'Framer': 'https://www.vectorlogo.zone/logos/framer/framer-icon.svg',
    'Cloudflare': 'https://www.vectorlogo.zone/logos/cloudflare/cloudflare-icon.svg',
    'Next.js': 'https://www.vectorlogo.zone/logos/nextjs/nextjs-icon.svg',
    'Cursor': 'https://www.cursor.com/favicon.ico',
    'Antigravity': 'https://www.svgrepo.com/show/452230/jar-with-lid.svg'
  };
  return icons[name] || 'https://www.svgrepo.com/show/361181/generic-folder.svg';
};

const getColor = (name) => {
  const colors = {
    'Strategy & Execution': '#854CE6',
    'Product Analytics': '#00C851',
    'Development Hub': '#3399FF',
    'AI Engineering': '#FF8800',
    'Agile & Ops': '#FF4444',
    'Modern Stack': '#AA66CC',
  };
  return colors[name] || '#854CE6';
};

const TechStack = () => {
  return (
    <Container id="tech-stack">
      <Wrapper>
        <Header>
           <Title
             as={motion.h2}
             initial={{ opacity: 0, y: 20 }}
             whileInView={{ opacity: 1, y: 0 }}
             viewport={{ once: true }}
             transition={{ duration: 0.6 }}
           >
             Tech Stack
           </Title>
           <Desc
             as={motion.p}
             initial={{ opacity: 0, y: 20 }}
             whileInView={{ opacity: 1, y: 0 }}
             viewport={{ once: true }}
             transition={{ duration: 0.6, delay: 0.2 }}
           >
             Professional ecosystem tools and platforms I use to build high fidelity products.
           </Desc>
        </Header>

        <TechRail
           as={motion.div}
           initial={{ opacity: 0 }}
           whileInView={{ opacity: 1 }}
           viewport={{ once: true }}
           transition={{ duration: 1 }}
        >
          <RailContent>
            {[...topTools, ...topTools].map((tool, index) => (
              <TechCard key={index}>
                <IconImage src={tool.icon} alt={tool.name} />
                <span style={{ color: '#FFF', fontSize: '15px', fontWeight: '600', letterSpacing: '0.01em' }}>{tool.name}</span>
              </TechCard>
            ))}
          </RailContent>
        </TechRail>

        <CategoryGrid>
          {categories.map((cat, index) => (
            <CategoryCard
              key={index}
              delay={cat.delay}
              as={motion.div}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <CategoryTitle style={{ color: getColor(cat.title) }}>{cat.title}</CategoryTitle>
              <TagContainer>
                {cat.tags.map((tag, idx) => {
                  const icon = getIcon(tag);
                  return (
                    <Tag key={idx} style={{ borderColor: getColor(cat.title) + '22' }}>
                      {icon && <img src={icon} alt="" style={{ width: '16px', height: '16px', borderRadius: '2px' }} />}
                      {tag}
                    </Tag>
                  );
                })}
              </TagContainer>
            </CategoryCard>
          ))}
        </CategoryGrid>
      </Wrapper>
    </Container>
  );
};

export default TechStack;
